import { useEffect, useRef, useState } from 'react';
import io from 'socket.io-client';
import AuthService from '../services/authService';
import { debounce, find, get, isEmpty } from 'lodash';
import {
    isConnectedWebSocketAfterSomeTimesState,
    isSocketConnect,
    retryWebSocket,
    isPaymentSuccessWebSocketState,
    isPaymentFailureWebSocketState,
    websocketPaymentTransactionIdState,
    isPaymentTimeOutWebSocketState,
    websocketMerchantOrderIdState,
    merchantDetailsState,
    totalAddOnsListState,
} from '../global/jotaiState';
import { useAtom } from 'jotai';
import toast from 'react-hot-toast';
import Auth_API from '../services/auth';
import { ErrorConstants } from '../constants/ErrorConstants';
import { StorageConstants } from '../constants/AppConstants';
import ObjectStorage from '../modules/ObjectStorage';

export default function WebSocket() {
    const selectedStore = AuthService.getStoreId();
    const selectedTerminal = AuthService.getTerminalId();
    const [totalAddOnsList, setTotalAddOnsList] = useAtom(totalAddOnsListState);

    const [socket, setSocket] = useState(null);
    const [retryCount, setRetryCount] = useAtom(retryWebSocket);
    const [isConnected, setIsConnected] = useAtom(isSocketConnect);
    const [isPaymentSuccessWebSocket, setIsPaymentSuccessWebSocket] = useAtom(isPaymentSuccessWebSocketState);
    const [isPaymentFailureWebSocket, setIsPaymentFailureWebSocket] = useAtom(isPaymentFailureWebSocketState);
    const [websocketPaymentTransactionId, setWebsocketPaymentTransactionId] = useAtom(websocketPaymentTransactionIdState);
    const [websocketMerchantOrderId, setWebsocketMerchantOrderId] = useAtom(websocketMerchantOrderIdState);
    const [isPaymentTimeOutWebSocket, setIsPaymentTimeOutWebSocket] = useAtom(isPaymentTimeOutWebSocketState);
    const [merchantDetails, setMerchantDetails] = useAtom(merchantDetailsState);
    const retryRef = useRef(0);
    const [isConnectedWebSocketAfterSomeTimes, setIsConnectedWebSocketAfterSomeTimes] =
        useAtom(isConnectedWebSocketAfterSomeTimesState);
    const RECONNECT_DELAYS = [10, 20, 40, 60, 120, 300, 600, 1200, 2400, 3600, 7200, 14400];
    const reconnectTimeout = useRef();
    const isManualReconnect = useRef(false);
    const API = AuthService.getRemoteURL();

    const handleRetry = () => {
        if (retryCount >= 6) return;
        setRetryCount((prev) => prev + 1);
    };

    const getStoreReferenceId = async () => {
        try {
            const res = await Auth_API.getStoreReferenceFromApi();
            const reference = find(
                res?.data,
                (item) => item.storeId === selectedStore
            )?.storeReference;
            AuthService.setStoreReference(reference);
            handleRetry();
        } catch (e) {
            toast.error(e?.errorResponse?.message || ErrorConstants.SOMETHING_WRONG);
        }
    };


    const connectWebSocket = () => {
        const accessToken = !isEmpty(merchantDetails) ? merchantDetails?.accessToken : AuthService._getAccessToken();
        const storeReference = !isEmpty(merchantDetails) ? merchantDetails?.merchantDetails?.storeReference : AuthService.getStoreReference();
        if (accessToken && !storeReference) {
            getStoreReferenceId();
        }
        if (!accessToken || !storeReference) return;

        const newSocket = io(API, {
            auth: { token: accessToken },
            reconnection: false,
            transports: ['websocket'],
        });
        newSocket.on('connect', () => {
            console.log('WebSocket connected');
            setIsConnected(true);
            clearTimeout(reconnectTimeout.current);
            isManualReconnect.current = false;
            retryRef.current = 0;
            newSocket.emit('joinStore', {
                storeReference: storeReference,
                terminalId: selectedTerminal,
            });
        });

        newSocket.on('message', (data) => {
            retryRef.current = 0;
            console.log('message got from websocket', data);
            if (get(data, 'decodeJson.code') === "PAYMENT_ERROR" && get(data, 'decodeJson.data.paymentState') === "FAILED") {
                if (get(data, 'decodeJson.data.payResponseCode') === "TXN_AUTO_FAILED") {
                    setIsPaymentTimeOutWebSocket(true);
                    setIsPaymentFailureWebSocket(true);
                    setWebsocketPaymentTransactionId(get(data, 'decodeJson.data.transactionId') || '');
                } else {
                    setIsPaymentFailureWebSocket(true);
                    setWebsocketPaymentTransactionId(get(data, 'decodeJson.data.transactionId') || '');
                }
            }
            if (get(data, 'decodeJson.code') === "PAYMENT_SUCCESS" && get(data, 'decodeJson.data.paymentState') === "COMPLETED") {
                setIsPaymentSuccessWebSocket(true);
                setWebsocketPaymentTransactionId(get(data, 'decodeJson.data.transactionId') || '');
                setWebsocketMerchantOrderId(get(data, 'decodeJson.data.merchantOrderId') || '');
            }

            if (data.type === "STOCK_OPT_UPDATE") {
                const { data: allAddOnsList = [] } =
                  ObjectStorage.getItem(StorageConstants.ALL_ADDONS_LIST) || {};
        
                const updates = data?.stockOptData || [];
        
                const updatedList = allAddOnsList?.map((item) => {
                  const found = updates?.find((u) => u?.optionId === item?.optionId);
        
                  if (found) {
                    return {
                      ...item,
                      stockQuantity: found?.newQuantity,
                    };
                  }
        
                  return item;
                });
        
                setTotalAddOnsList(updatedList);
        
                ObjectStorage.setItem(StorageConstants.ALL_ADDONS_LIST, {
                  data: updatedList,
                });
              }
            
        });

        newSocket.on('disconnect', () => {
            console.error('WebSocket disconnected');
            setIsConnected(false);
            if (retryRef.current < RECONNECT_DELAYS.length && !isManualReconnect.current) {
                scheduleReconnect();
            }
        });

        newSocket.on('connect_error', (error) => {
            setIsConnected(false);
            if (retryRef.current < RECONNECT_DELAYS.length && !isManualReconnect.current) {
                scheduleReconnect();
            }
            console.error('WebSocket connection error:', error);
        });
        setSocket(newSocket);
    };

    const handleReconnection = () => {
        if (retryRef.current < 3) {
            retryRef.current += 1;
            setTimeout(connectWebSocket, 10000);
        }
    };

    const scheduleReconnect = () => {
        const delay = RECONNECT_DELAYS[retryRef.current] * 1000;
        console.log(`Retry ${retryRef.current + 1} in ${delay / 1000} seconds...`);
        clearTimeout(reconnectTimeout.current);
        reconnectTimeout.current = setTimeout(() => {
            if (!isConnected) {
                retryRef.current += 1;
                connectWebSocket();
            }
        }, delay);
    };

    useEffect(() => {
        if (retryCount > 0) {
            isManualReconnect.current = true;
            connectWebSocket();
        }
    }, [retryCount]);

    useEffect(() => {
        if (selectedStore && selectedTerminal) {
            setTimeout(() => {
                setIsConnectedWebSocketAfterSomeTimes(true);
            }, 1000);
        }
    }, [selectedStore, selectedTerminal]);

    useEffect(() => {
        if (isConnectedWebSocketAfterSomeTimes) {
            const debounced = debounce(() => {
                connectWebSocket();
            }, 1000);

            debounced();

            return () => {
                debounced.cancel();
                if (socket) {
                    socket.emit('leaveStore');
                    socket.disconnect();
                }
            };
        }
    }, [isConnectedWebSocketAfterSomeTimes]);

    return null;
}
